import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Scale,
  Target,
  Shield,
  Users,
  MessageCircle,
  CheckCircle2,
  Award,
  BookOpen,
  Briefcase,
} from "lucide-react";

const LAWYER_AUTHORITY = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/bgw8breo_IMG_7021.jpg";
const LAWYER_SECONDARY = "https://customer-assets.emergentagent.com/job_6c4f233a-9fd9-45dd-862d-36bf6aff1fc0/artifacts/yzaetzq0_IMG_7110.jpg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de agendar uma consulta jurídica.";

const SobrePage = () => {
  const valores = [
    { icon: <Scale className="w-6 h-6" />, title: "Ética", desc: "Atuação pautada pelos princípios da advocacia e respeito ao cliente" },
    { icon: <Target className="w-6 h-6" />, title: "Compromisso", desc: "Dedicação integral a cada caso, buscando sempre o melhor resultado" },
    { icon: <Shield className="w-6 h-6" />, title: "Responsabilidade", desc: "Transparência e seriedade em todas as etapas do processo" },
    { icon: <Users className="w-6 h-6" />, title: "Humanização", desc: "Atendimento personalizado e compreensão da realidade de cada cliente" },
  ];

  return (
    <>
      <Helmet>
        <title>Sobre o Escritório | Helio Lopes Leal Advocacia</title>
        <meta name="description" content="Conheça o escritório Helio Lopes Leal Advocacia. Experiência, estratégia e compromisso com resultados." />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[600px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-blue-900 font-medium tracking-wider text-sm mb-4">SOBRE O ESCRITÓRIO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Experiência, Estratégia e Compromisso com{" "}
              <span className="text-blue-900">Resultados</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Advocacia estratégica e consultoria jurídica com atuação técnica 
              e personalizada para pessoas e empresas.
            </p>
          </div>
        </section>

        {/* História */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Image */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -inset-4 bg-blue-900/5 rounded-2xl transform -rotate-2"></div>
                  <img
                    src={LAWYER_AUTHORITY}
                    alt="Dr. Helio Lopes Leal"
                    className="relative rounded-2xl shadow-xl w-full max-w-lg mx-auto object-cover"
                    style={{ aspectRatio: "4/5" }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen className="w-6 h-6 text-blue-900" />
                  <p className="text-blue-900 font-medium tracking-wider text-sm">TRAJETÓRIA PROFISSIONAL</p>
                </div>
                <h2 
                  className="text-3xl font-bold text-gray-900 mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Dr. Helio Lopes Leal
                </h2>
                
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    A advocacia faz parte da história da família Lopes Leal. Crescer em um ambiente onde 
                    o Direito sempre esteve presente — nas conversas, nos valores e no exemplo de dedicação 
                    à justiça — despertou desde cedo a vocação para a carreira jurídica. Essa herança 
                    familiar se traduz em um compromisso natural com a ética, a responsabilidade e a 
                    busca incansável pela defesa dos direitos de cada cliente.
                  </p>
                  <p>
                    Graduado em Direito pela <strong>Faculdades Metropolitanas Unidas (FMU)</strong>, 
                    com formação sólida e comprometimento com a excelência acadêmica e profissional.
                  </p>
                  <p>
                    Ao longo de sua trajetória, desenvolveu experiência prática significativa em 
                    <strong> audiências</strong>, <strong>sustentação oral</strong> e atuação perante 
                    tribunais, consolidando-se como profissional de confiança na defesa de trabalhadores 
                    e empresas.
                  </p>
                  <p>
                    Sua atuação é marcada pela <strong>visão estratégica</strong>, com foco na prevenção 
                    de litígios e <strong>gestão de riscos jurídicos</strong>. Cada caso é tratado de 
                    forma única, com planejamento processual personalizado e comunicação transparente 
                    com o cliente.
                  </p>
                  <p>
                    O escritório atende pessoas físicas e jurídicas nas áreas de Direito do Trabalho, 
                    Imobiliário, Tributário, Previdenciário, Cível, Família e Penal, sempre com o compromisso 
                    de oferecer <strong>soluções jurídicas seguras e eficientes</strong>.
                  </p>
                </div>

                <div className="mt-8 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-900">
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-900">OAB/SP 396.732</strong> — Inscrito na Ordem dos 
                    Advogados do Brasil, Seccional São Paulo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Segunda Imagem - Escritório */}
        <section className="py-16 bg-gray-50 relative overflow-hidden">
          <div className="absolute right-0 top-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 
                  className="text-2xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Atendimento Personalizado
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Acreditamos que cada cliente merece atenção individual e dedicada. Por isso, 
                  oferecemos um atendimento direto com o advogado responsável, garantindo que 
                  você seja ouvido e compreendido em todas as etapas do seu processo.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Nossa abordagem combina a tradição e os valores herdados de uma família de 
                  advogados com técnicas modernas de gestão jurídica, resultando em soluções 
                  eficientes e adaptadas à realidade de cada caso.
                </p>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-900/5 rounded-2xl transform rotate-2"></div>
                <img
                  src={LAWYER_SECONDARY}
                  alt="Dr. Helio Lopes Leal - Atendimento"
                  className="relative rounded-2xl shadow-xl w-full object-cover"
                  style={{ aspectRatio: "4/3" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Missão, Visão, Valores */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Missão */}
              <div className="bg-white p-8 rounded-xl border border-gray-100">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Missão</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Oferecer soluções jurídicas seguras e estratégicas, com atendimento personalizado 
                  e foco na proteção dos direitos e interesses de nossos clientes.
                </p>
              </div>

              {/* Visão */}
              <div className="bg-white p-8 rounded-xl border border-gray-100">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-6">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Visão</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Ser referência em advocacia estratégica, reconhecido pela atuação 
                  técnica, moderna e comprometida com resultados.
                </p>
              </div>

              {/* Valores */}
              <div className="bg-white p-8 rounded-xl border border-gray-100">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-6">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Valores</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Ética, responsabilidade, comprometimento, transparência e respeito ao cliente 
                  em todas as etapas da atuação profissional.
                </p>
              </div>
            </div>

            {/* Valores Grid */}
            <div className="max-w-4xl mx-auto">
              <h3 
                className="text-2xl font-bold text-gray-900 text-center mb-10"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Princípios que Guiam Nossa Atuação
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {valores.map((valor, index) => (
                  <div key={index} className="flex gap-4 p-6 bg-white rounded-lg border border-gray-100">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-900 flex-shrink-0">
                      {valor.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{valor.title}</h4>
                      <p className="text-gray-600 text-sm">{valor.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Áreas de Atuação Resumo */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Áreas de Atuação
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Oferecemos assessoria jurídica completa nas principais áreas do Direito
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { name: "Trabalhista", path: "/direito-trabalhista" },
                { name: "Imobiliário", path: "/direito-imobiliario" },
                { name: "Tributário", path: "/direito-tributario" },
                { name: "Previdenciário", path: "/direito-previdenciario" },
                { name: "Cível", path: "/direito-civel" },
                { name: "Família", path: "/direito-familia" },
              ].map((area, index) => (
                <Link
                  key={index}
                  to={area.path}
                  className="p-4 text-center bg-gray-50 hover:bg-blue-900 rounded-lg transition-all group"
                >
                  <span className="text-sm font-medium text-gray-700 group-hover:text-white transition-colors">
                    {area.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-blue-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 
              className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Vamos Conversar Sobre o Seu Caso?
            </h2>
            <p className="text-blue-200 mb-8 max-w-xl mx-auto">
              Entre em contato e agende uma consulta. Estamos prontos para oferecer 
              a orientação jurídica que você precisa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Falar com Advogado
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all"
              >
                Ver Todas as Formas de Contato
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SobrePage;
